import { useCallback, useEffect, useState, useRef } from "react";
import { useDolfinClient, useDolfinAuth } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { MoreHorizontal, Plus, SearchIcon, Trash2, UsersRound } from "lucide-react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { formatDate } from "@/lib/format";
import { AddUserDialog } from "@/components/AddUserDialog";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import {
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
} from "@/components/PageUi";
import { InfoHint } from "@/components/InfoHint";
import { usePermissions } from "@/lib/permissions";
import { toast } from "sonner";

type OrganisationUserResponse = components["schemas"]["OrganisationUserResponse"];
type OrganisationRole = components["schemas"]["OrganisationRole"];

const PAGE_SIZE = 20;
const ROLES: OrganisationRole[] = ["Member", "Admin", "Owner"];

function roleBadgeVariant(role: OrganisationRole): "default" | "secondary" | "outline" {
  if (role === "Owner") return "default";
  if (role === "Admin") return "secondary";
  return "outline";
}

export interface UsersListFilters {
  email?: string;
}

export interface UsersPageProps {
  /** Initial filters. Updates to filters live in component state. */
  defaultFilters?: UsersListFilters;
  onFiltersChange?: (filters: UsersListFilters) => void;
}

export function UsersPage({ defaultFilters, onFiltersChange }: UsersPageProps) {
  const api = useDolfinClient();
  const { auth } = useDolfinAuth();
  const { can } = usePermissions();

  const [users, setUsers] = useState<OrganisationUserResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [emailInput, setEmailInput] = useState(defaultFilters?.email ?? "");
  const debouncedEmail = useDebouncedValue(emailInput);

  const [updatingUserId, setUpdatingUserId] = useState<string | null>(null);
  const [userToRemove, setUserToRemove] = useState<OrganisationUserResponse | null>(null);

  const canManage = can("manageUsers");

  // Ref so a fresh inline callback doesn't re-fire the effect (an emit loop via e.g. setSearchParams).
  const onFiltersChangeRef = useRef(onFiltersChange);
  onFiltersChangeRef.current = onFiltersChange;
  const firstEmit = useRef(true);

  useEffect(() => {
    if (firstEmit.current) {
      // Host already passed these values as defaultFilters; nothing to sync.
      firstEmit.current = false;
      return;
    }
    onFiltersChangeRef.current?.({
      email: debouncedEmail || undefined,
    });
  }, [debouncedEmail]);

  const fetchUsers = useCallback(async (cursor?: string) => {
    setLoading(true);
    setError(null);

    try {
      const query: Record<string, string | number> = { Limit: PAGE_SIZE };
      if (debouncedEmail) query.Email = debouncedEmail;
      if (cursor) query.Cursor = cursor;

      const { data, error: apiError } = await api.GET("/v1/users", {
        params: { query },
      });

      if (apiError || !data) {
        console.error("Failed to load users:", apiError);
        setError("Couldn't load the people in your organisation.");
        return;
      }

      setUsers(data.data ?? []);
      const next = data.meta?.cursors?.next ?? null;
      setNextCursor(next);
      setHasMore(next !== null);
    } catch (e) {
      console.error("Failed to load users:", e);
      setError("Couldn't load the people in your organisation.");
    } finally {
      setLoading(false);
    }
  }, [api, debouncedEmail]);

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchUsers();
  }, [fetchUsers]);

  function refresh() {
    setCursorStack([]);
    setNextCursor(null);
    fetchUsers();
  }

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchUsers(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setCursorStack(newStack);
    fetchUsers(prevCursor);
  }

  async function changeRole(target: OrganisationUserResponse, newRole: OrganisationRole) {
    if (newRole === target.role) return;
    if (!target.id) return;
    const id = target.id;
    setUpdatingUserId(id);
    const { error: apiError } = await api.PATCH("/v1/users/{id}/role", {
      params: { path: { id } },
      body: { role: newRole },
    });
    setUpdatingUserId(null);

    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to update role"));
      return;
    }

    toast.success(`Role updated to ${newRole}`);
    refresh();
  }

  async function confirmRemoveUser() {
    const target = userToRemove;
    if (!target?.id) return;
    const id = target.id;
    setUserToRemove(null);
    setUpdatingUserId(id);
    const { error: apiError } = await api.DELETE("/v1/users/{id}", {
      params: { path: { id } },
    });
    setUpdatingUserId(null);

    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to remove user"));
      return;
    }

    toast.success("User removed");
    refresh();
  }

  const currentPage = cursorStack.length + 1;

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Organisation"
        title="Users"
        subtitle="Everyone who can sign in to this organisation, and what they are allowed to do."
        actions={
          canManage && (
            <Button onClick={() => setCreateOpen(true)} className="w-full gap-1.5 sm:w-auto">
              <Plus className="size-4" />
              Add user
            </Button>
          )
        }
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="relative w-full sm:w-72">
          <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="text"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            className="h-9 pl-8"
            placeholder="Search by email"
            aria-label="Search by email"
          />
        </div>
        {!loading && !error && users.length > 0 ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            {users.length} {users.length === 1 ? "person" : "people"}
            {hasMore || currentPage > 1 ? " on this page" : ""}
          </span>
        ) : null}
      </div>

      {loading ? (
        <LoadingState label="Loading users..." />
      ) : error ? (
        <ErrorState message={error} onRetry={refresh} />
      ) : users.length === 0 ? (
        debouncedEmail ? (
          <EmptyState icon={SearchIcon} title="No one matches that email">
            Nobody in this organisation has an email containing "{debouncedEmail}".
          </EmptyState>
        ) : (
          <EmptyState
            icon={UsersRound}
            title="No users yet"
            action={
              canManage ? (
                <Button size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus className="size-4" /> Add user
                </Button>
              ) : undefined
            }
          >
            Invite the people who approve bills, send invoices or manage settings.
          </EmptyState>
        )
      ) : (
        <>
          <TableShell>
            <Table>
              <TableHeader>
                <HeaderRow>
                  <TableHead className={TH}>Person</TableHead>
                  <TableHead className={TH}>
                    <span className="inline-flex items-center gap-1">
                      Role
                      <InfoHint label="roles">
                        Members can view and prepare work. Admins can also approve and pay bills, send
                        invoices, and manage users and settings. Owners can do all of that and connect a bank
                        account.
                      </InfoHint>
                    </span>
                  </TableHead>
                  <TableHead className={`text-right ${TH}`}>Joined</TableHead>
                  <TableHead className="w-[48px]" />
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => {
                  const isSelf = u.id === auth?.userId;
                  const isUpdating = updatingUserId === u.id;
                  const showMenu = canManage && !isSelf;
                  return (
                    <TableRow key={u.id}>
                      <TableCell>
                        <EntityCell
                          name={u.name || u.email}
                          secondary={u.name ? u.email : undefined}
                          badges={
                            isSelf ? (
                              <span className="rounded-full border px-1.5 py-px text-[10px] font-medium text-muted-foreground">
                                You
                              </span>
                            ) : undefined
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Badge variant={roleBadgeVariant(u.role)}>{u.role}</Badge>
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground tabular-nums">
                        {formatDate(u.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        {showMenu && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                disabled={isUpdating}
                                className="size-8"
                                aria-label={`Actions for ${u.email}`}
                              >
                                {isUpdating ? (
                                  <Spinner className="size-4" />
                                ) : (
                                  <MoreHorizontal className="size-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                                Change role
                              </DropdownMenuLabel>
                              <DropdownMenuRadioGroup
                                value={u.role}
                                onValueChange={(v) => changeRole(u, v as OrganisationRole)}
                              >
                                {ROLES.map((r) => (
                                  <DropdownMenuRadioItem key={r} value={r}>
                                    {r}
                                  </DropdownMenuRadioItem>
                                ))}
                              </DropdownMenuRadioGroup>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-destructive focus:text-destructive"
                                onClick={() => setUserToRemove(u)}
                              >
                                <Trash2 className="mr-2 size-4" />
                                Remove from organisation
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableShell>

          <Pager
            page={currentPage}
            hasPrevious={currentPage > 1}
            hasNext={hasMore}
            onPrevious={handlePrevPage}
            onNext={handleNextPage}
          />
        </>
      )}

      <AddUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={refresh}
      />

      <AlertDialog
        open={userToRemove !== null}
        onOpenChange={(open) => !open && setUserToRemove(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove user from organisation?</AlertDialogTitle>
            <AlertDialogDescription>
              {userToRemove?.email} will lose access to this organisation. This can be undone by re-adding them later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmRemoveUser}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
